// A line taken from the user's own games, turned into something that can be practised.
//
// It is not a plain link: the same opening may already be a line of the catalog (which practises
// instantly, with pre-generated moves), may already be a line of theirs, or may not exist yet and
// have to be created. Working that out belongs here rather than in the view that happens to show
// the row — the dashboard, the study's picker and the picker inside the board page all show it.

import { Chess } from "#vendor/chessjs/chess.js";
import { store } from "../state/index.ts";
import { K } from "../state/keys.ts";
import * as api from "./api.ts";
import * as catalog from "./catalog.ts";
import * as ownLines from "./ownLines.ts";
import * as toast from "../legacy/toast.js";
import { practiceHref } from "../domain/dashboardView.ts";
import { numberedFromUci } from "../domain/linePosition.ts";
import type { CatalogLine } from "./catalog.ts";
import { askStudyMode } from "../components/chrome/StudyChoice.tsx";

/** A line the server suggested from the user's games: the moves, the side, and a name for it. */
export interface Suggestion {
    uciMoves: string;
    color: string;
    name?: string | null;
}

/** A line of the user's own, as the API answers. */
interface OwnLine {
    id: number;
    name: string;
    color: string;
    fen?: string;
    uciMoves?: string;
    origin?: string;
}

/**
 * The catalog line a suggestion *is*: same side, same moves, and nothing shorter.
 *
 * The one it is inside is a different thing and a wrong answer here. The catalog names openings and
 * a row of the ranking is a branch of one, so "the Löwenthal is costing you points" shares its first
 * three moves with the Open Sicilian — practising that is practising the position the player already
 * reached and not the one the row named, which is every row of a Sicilian leading to the same board.
 * When the catalog has no line at exactly this position, one gets made at it.
 */
export function sameCatalogLine(catalogLines: CatalogLine[], suggestion: Suggestion): CatalogLine | null {
    const moves = suggestion.uciMoves.trim();
    return catalogLines.find((line) => line.color === suggestion.color && line.uci.trim() === moves) || null;
}

// The daily plan's question ("is there anything ready that covers this"), which is a rule of the plan
export { relatedCatalogLine } from "../domain/catalogMatch.ts";

/** A double click must not create the same line twice. */
let adopting = false;

/**
 * Practises a suggestion. In order: the catalog line that is exactly it, the line of theirs that
 * already is it, or — asked first — the line studied once or kept as theirs.
 *
 * @param start  what to do with the line once it is known. Given by the board page, which starts it
 *               where it stands; left out everywhere else, which goes to the study instead.
 * @param origin what a line made for it says it came from: the user's games, unless they chose it
 *               themselves (the study list)
 */
export async function practiceSuggestion(suggestion: Suggestion | null | undefined,
                                         { start, origin = "games" }: {
                                             start?: (line: unknown) => void; origin?: "manual" | "games";
                                         } = {}): Promise<void> {
    if (!suggestion || !suggestion.uciMoves) return;
    const related = sameCatalogLine(await catalog.getLines(), suggestion);
    if (related) {
        if (!start) {
            window.location.href = practiceHref(related.slug, "study");
            return;
        }
        start({ ...related, source: "catalog" });
        return;
    }
    const same = (await theirs())
        .filter((line) => (line.uciMoves || "").trim() === suggestion.uciMoves && line.color === suggestion.color);
    const kept = same.find((line) => line.origin !== "once");
    if (kept) {
        openOwnLine(kept, start);
        return;
    }
    // Nothing of theirs yet: whether it becomes one of their lines is theirs to say, before it is made
    const mode = await askStudyMode(suggestion.name || "", numberedFromUci(suggestion.uciMoves));
    if (!mode) return;
    const once = same.find((line) => line.origin === "once");
    if (once) {
        if (mode === "keep") await keep(once);
        openOwnLine(once, start);
        return;
    }
    await adoptSuggestion(suggestion, start, mode === "once" ? "once" : origin);
}

/** A line studied once becomes one of theirs, and every list of their lines shows it. */
async function keep(line: OwnLine): Promise<void> {
    try {
        await api.keepLine(line.id);
        await ownLines.refresh();
    } catch (err) {
        toast.showToast({ title: "Could not add the line", message: (err as Error).message, type: "error", duration: 6000 });
    }
}

/**
 * The lines they already have, whether or not the page they are on lists them — the ones studied once too,
 * which no list shows: practising one again must not make it twice.
 *
 * It used to read the legacy module's copy, which is only filled on the pages that wire it up: on
 * the study's picker it was always empty, so every click on a row of their games made the same
 * line again. A line put aside counts too — practising it is what the click asked for, and a
 * second copy of it is not what "I already have this" means.
 */
async function theirs(): Promise<OwnLine[]> {
    if (!store.get(K.user.currentUser)) return [];
    return await api.fetchLines(true).catch(() => []) as OwnLine[];
}

/** Puts one of the user's own lines on the board, or opens the study on it. */
export function openOwnLine(line: OwnLine, start?: (line: unknown) => void): void {
    if (!start) {
        window.location.href = practiceHref(`line-${line.id}`, "study");
        return;
    }
    start({
        source: "user", id: line.id, name: line.name, color: line.color || "white",
        fen: line.fen, uci: line.uciMoves,
    });
}

/** Saves a suggestion as a line of the user, and then practises it like any other. */
export async function adoptSuggestion(suggestion: Suggestion, start?: (line: unknown) => void,
                                      origin: "manual" | "games" | "once" = "games"): Promise<void> {
    if (adopting) return;
    if (!store.get<boolean>("user.lichessConnected")) {
        toast.showToast({
            title: "Connect Lichess first", type: "info", duration: 8000,
            message: "Lines of your own get their sparring moves live from Lichess. Connect your account from the "
                + "user menu, it is free.",
        });
        return;
    }
    // The opening, not the moves: "My games: 1.e4 c5 2.c3 Nf6 3.e5" told nobody which line it was,
    // and five of them in a list are indistinguishable. The server names the position; this uses it.
    const name = (suggestion.name || `My games: ${numberedFromUci(suggestion.uciMoves)}`).slice(0, 150);
    adopting = true;
    try {
        const line = await api.createLine(name, new Chess().fen(), suggestion.uciMoves, suggestion.color,
            origin) as OwnLine;
        if (origin !== "once") await ownLines.refresh();
        openOwnLine(line, start);
    } catch (err) {
        console.error("Failed to create the line", err);
        toast.showToast({
            title: "Could not create the line", message: (err as Error).message, type: "error", duration: 6000,
        });
    } finally {
        adopting = false;
    }
}
